import type { Maybe } from 'graphql/jsutils/Maybe'
import type { ImageModel } from '@/components/ImageView'
import type { ImageFields } from '@/service/fragments/imageFieldsFrag'
import { createMediaCentralUriBuilder } from '@amzn/media-central-url'
import { optimizeFourSquareArt } from '@/util/optimizeFourSquareArt'

// https://w.amazon.com/bin/view/MSA/HowTo/ImageStyleCodes

export type ImageStyleOptions = {
    /**
     * This tag takes an image and scales it to a square canvas. If the image's dimensions aren't square to begin with
     * then it will place it upon a transparent background and center the scaled source image. You will see a white
     * background if your image format is JPEG.
     *
     * Note that for historical reasons there are two different tags for this feature (AA and SS), but they yield
     * identical results.
     */
    readonly scaleToSquare?: Maybe<number>

    /**
     * This tag proportionally scales an image in the longest dimension to a specified value. The image can only be
     * scaled down (i.e. if the specified value is larger than both the current width and height, then the image will
     * not be scaled), and you can only use the 'Large' image size as the base.
     */
    readonly scaleToLongest?: Maybe<number>

    /**
     * This tag proportionally scales an image to a specified width. The image can only be scaled down (i.e. if the
     *  specified width is larger than the current width, then the image will not be scaled), and you can only use the
     * 'Large' image size as the base.
     */
    readonly scaleToWidth?: Maybe<number>

    /**
     * This tag proportionally scales an image to a specified height. The image can only be scaled down (i.e. if the
     * specified height is larger than the current height, then the image will not be scaled), and you can only use the
     * 'Large' image size as the base.
     */
    readonly scaleToHeight?: Maybe<number>
}

/**
 * Additional scaling sizes to provide to the src set.
 */
const scalings = [2] as const

function scaleNum(num: Maybe<number>, scaling: number): number | null {
    if (num == null) return null
    return num * scaling
}

/**
 * Given a GQL image and sizing instructions, prepares an image model for use in an ImageView.
 *
 * @param image
 * @param options
 */
export function createImageModel(
    image: Maybe<ImageFields>,
    options: ImageStyleOptions
): ImageModel {
    const baseImage = modifyImage(image, options)
    const { url, width, height } = baseImage
    const srcset: string[] = url ? [`${url} 1x`] : []
    for (const scaling of scalings) {
        const scaledImage = modifyImage(image, {
            scaleToHeight: scaleNum(options.scaleToHeight, scaling),
            scaleToWidth: scaleNum(options.scaleToWidth, scaling),
            scaleToLongest: scaleNum(options.scaleToLongest, scaling),
            scaleToSquare: scaleNum(options.scaleToSquare, scaling),
        })
        if (scaledImage.url) {
            srcset.push(`${scaledImage.url} ${scaling}x`)
        }
    }

    return {
        src: url,
        srcset: srcset.join(', '),
        width,
        height,
    }
}

/**
 * Given a GQL image and sizing instructions, returns a modified image.
 *
 * @param image
 * @param options
 */
export function modifyImage(image: Maybe<ImageFields>, options: ImageStyleOptions): ImageFields {
    let { url, width, height } = image ?? {}
    const imageType = image?.imageType
    if (options.scaleToSquare) {
        width = options.scaleToSquare
        height = options.scaleToSquare
    }
    if (options.scaleToLongest) {
        if (!width || !height) {
            width = options.scaleToLongest
            height = options.scaleToLongest
        } else if (width >= height) {
            height *= options.scaleToLongest / width
            width = options.scaleToLongest
        } else {
            width *= options.scaleToLongest / height
            height = options.scaleToLongest
        }
    }
    if (options.scaleToWidth) {
        if (!width || !height) {
            width = options.scaleToWidth
        } else {
            height *= options.scaleToWidth / width
            width = options.scaleToWidth
        }
    }
    if (options.scaleToHeight) {
        if (!width || !height) {
            height = options.scaleToHeight
        } else {
            width *= options.scaleToHeight / height
            height = options.scaleToHeight
        }
    }

    const builder = createMediaCentralUriBuilder(url)
    if (!builder) {
        // Cannot modify tags, either not a media central image or not a well-formed URI.
        return {
            url: url ?? null,
            width: width ?? null,
            height: height ?? null,
            imageType: imageType ?? null,
        }
    }
    // Strip the expensive 2800px-upscale-and-blur background from MUPS
    // 4-square collages before sizing — saves a huge amount of bandwidth on
    // first paint. Mirrors green hornet's getSquareImageSource pipeline.
    optimizeFourSquareArt(builder)
    if (options.scaleToSquare) {
        builder.scaleToSquare(options.scaleToSquare)
    }
    if (options.scaleToLongest) {
        builder.scaleToLongest(options.scaleToLongest)
    }
    if (options.scaleToWidth) {
        builder.scaleToWidth(options.scaleToWidth)
    }
    if (options.scaleToHeight) {
        builder.scaleToHeight(options.scaleToHeight)
    }
    url = builder.toUri()
    return {
        url,
        width: width ?? null,
        height: height ?? null,
        imageType: imageType ?? null,
    }
}
